import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useOutletContext } from "react-router-dom";
import type { User } from "firebase/auth";
import { signOut } from "../firebase";
import {
  ensureServer,
  ensureWorkspace,
  errorMessage,
  getCatalog,
  setConnectionEnabled,
  watchActivity,
  watchIncomingInvites,
  watchProjectConnections,
  watchProjects,
  watchServer,
  watchTeamInvites,
  watchTeams,
} from "../api";
import {
  enablementFor,
  scopeOf,
  slugOf,
  toolsForConnection,
  type CallLog,
  type CatalogProvider,
  type Connection,
  type Invite,
  type Project,
  type ServerDoc,
  type Team,
} from "../types";
import Toast, { type ToastState } from "../components/Toast";
import TopBar from "../components/TopBar";

/** Where the last-used project is remembered between visits. */
const PROJECT_KEY = "mochi.projectId";

/**
 * What every page reads, loaded once here in the shell that wraps them.
 *
 * The shell owns the *workspace* (teams, projects, which project is current) and the
 * per-project data (the caller's server there, the connections it can see, activity).
 * Switching projects swaps the second set and leaves the first alone; crossing between
 * pages refetches nothing.
 */
export interface DashboardContext {
  user: User;
  teams: Team[];
  projects: Project[];
  /** The project everything on screen belongs to. Null only while booting. */
  project: Project | null;
  selectProject: (projectId: string) => void;
  /** Invitations addressed to the signed-in email, live. */
  incomingInvites: Invite[];
  /** Outstanding invites of the user's teams, for the team cards. */
  teamInvites: Invite[];
  server: (ServerDoc & { id: string }) | null;
  token: string | null;
  onToken: (token: string | null) => void;
  /** The server's OAuth client credentials, for configs that take a pasted id + secret. */
  oauthClient: { id: string; secret: string } | null;
  catalog: CatalogProvider[];
  connections: Connection[];
  activity: CallLog[];
  /** The slugs the server is set to expose. Not all of them are necessarily healthy. */
  enabled: Set<string>;
  /** Switched on *and* healthy: the accounts actually serving tools right now. */
  liveConnections: Connection[];
  toolCount: number;
  toggleConnection: (slug: string, on: boolean) => Promise<void>;
  notify: (message: string, kind?: ToastState["kind"]) => void;
}

/** Typed `useOutletContext`, so the pages do not each restate the shape above. */
export function useDashboard() {
  return useOutletContext<DashboardContext>();
}

export default function Dashboard({ user }: { user: User }) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  /** The default project from ensureWorkspace: the fallback whenever nothing is chosen. */
  const [defaultProjectId, setDefaultProjectId] = useState<string | null>(null);
  const [chosenProjectId, setChosenProjectId] = useState<string | null>(
    () => localStorage.getItem(PROJECT_KEY)
  );

  const [server, setServer] = useState<(ServerDoc & { id: string }) | null>(null);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [catalog, setCatalog] = useState<CatalogProvider[]>([]);
  const [activity, setActivity] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastState | null>(null);

  /**
   * The plaintext bearer token *for the current project's server*. Stored encrypted
   * server-side and handed back by `ensureServer` per project, so the install command is
   * copy-paste-ready. Cleared on every project switch, because each project has its own.
   */
  const [token, setToken] = useState<string | null>(null);

  /** The pre-registered OAuth client, same per-project lifecycle as the token above. */
  const [oauthClient, setOauthClient] = useState<{ id: string; secret: string } | null>(
    null
  );

  const notify = useCallback((message: string, kind: ToastState["kind"] = "ok") => {
    setToast({ message, kind });
  }, []);

  // Boot: ensure the workspace exists (which also migrates pre-project setups, keeping
  // their server id and token), and load the provider catalog. Once, on mount.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [wsRes, catalogRes] = await Promise.all([
          ensureWorkspace(),
          getCatalog(),
        ]);
        if (cancelled) return;
        setDefaultProjectId(wsRes.data.projectId);
        setCatalog(catalogRes.data.providers);
      } catch (err) {
        if (!cancelled) notify(errorMessage(err), "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [notify]);

  // The workspace itself, live: which teams the user is in, and their projects. The
  // personal team -- shown everywhere as "Private projects" -- sorts first, so private
  // always sits above the teams in the switcher and on the Teams page alike.
  useEffect(
    () =>
      watchTeams(user.uid, (next) =>
        setTeams(
          [...next].sort(
            (a, b) =>
              Number(b.personal ?? false) - Number(a.personal ?? false) ||
              a.name.localeCompare(b.name)
          )
        )
      ),
    [user.uid]
  );

  const teamIds = useMemo(() => teams.map((t) => t.id).sort().join(","), [teams]);
  useEffect(
    () => watchProjects(teamIds ? teamIds.split(",") : [], setProjects),
    [teamIds]
  );

  // Invitations, both directions: ones addressed to this user (they badge the Teams
  // link, so an invite is noticeable from any page) and outstanding ones on the
  // user's teams (shown on the team cards).
  const [incomingInvites, setIncomingInvites] = useState<Invite[]>([]);
  const [teamInvites, setTeamInvites] = useState<Invite[]>([]);
  useEffect(
    () => (user.email ? watchIncomingInvites(user.email, setIncomingInvites) : undefined),
    [user.email]
  );
  useEffect(
    () => watchTeamInvites(teamIds ? teamIds.split(",") : [], setTeamInvites),
    [teamIds]
  );

  /**
   * The current project, derived rather than stored: the explicit choice if it still
   * exists (it can vanish -- leaving a team takes its projects along), else the default
   * project, else whatever is first. Null only before anything has loaded.
   */
  const project = useMemo(() => {
    return (
      projects.find((p) => p.id === chosenProjectId) ??
      projects.find((p) => p.id === defaultProjectId) ??
      projects[0] ??
      null
    );
  }, [projects, chosenProjectId, defaultProjectId]);

  const selectProject = useCallback((projectId: string) => {
    setChosenProjectId(projectId);
    localStorage.setItem(PROJECT_KEY, projectId);
  }, []);

  // Everything below is per-project, so it re-runs (and the old listeners die) on switch.
  useEffect(() => {
    if (!project) return;

    setServer(null);
    setConnections([]);
    setActivity([]);
    setToken(null);
    setOauthClient(null);

    let cancelled = false;
    // The caller's server for this project -- created on first visit, same afterwards.
    // The token comes back decrypted so Setup can show a ready-to-paste install command.
    ensureServer({ projectId: project.id })
      .then((res) => {
        if (cancelled) return;
        if (res.data.token) setToken(res.data.token);
        if (res.data.oauthClientId) {
          setOauthClient({
            id: res.data.oauthClientId,
            secret: res.data.oauthClientSecret,
          });
        }
      })
      .catch((err) => {
        if (!cancelled) notify(errorMessage(err), "error");
      });

    const unsubs = [
      watchServer(user.uid, project.id, setServer),
      watchProjectConnections(
        user.uid,
        { id: project.id, teamId: project.teamId },
        setConnections
      ),
      watchActivity(user.uid, project.id, setActivity),
    ];
    return () => {
      cancelled = true;
      unsubs.forEach((u) => u());
    };
  }, [user.uid, project?.id, project?.teamId, notify]);

  /**
   * Which accounts are switched on, by slug -- per-member state for personal
   * connections, the shared team-wide switch for team ones, exactly as the MCP endpoint
   * will read them. Derived through `enablementFor` so the two can never disagree.
   */
  const enabled = useMemo(
    () =>
      new Set(
        connections.filter((c) => enablementFor(c, server).enabled).map(slugOf)
      ),
    [connections, server]
  );

  /**
   * One switch, one call. Optimistic UI is tempting here, but the Firestore listener
   * round-trips in well under 100ms and a wrong-then-right toggle flicker on a
   * security-relevant switch is worse than a brief delay.
   */
  const toggleConnection = useCallback(
    async (slug: string, on: boolean) => {
      const conn = connections.find((c) => slugOf(c) === slug);
      if (!conn) return;

      try {
        await setConnectionEnabled({ connectionId: conn.id, enabled: on });
        // A team switch is everyone's switch; say so, because the click looks the same
        // as a private one and its blast radius is not.
        if (scopeOf(conn) === "team") {
          notify(
            `${conn.accountLabel} switched ${on ? "on" : "off"} for everyone in the team.`
          );
        }
      } catch (err) {
        notify(errorMessage(err), "error");
      }
    },
    [connections, notify]
  );

  /** Every account that is switched on *and* healthy -- i.e. actually serving tools. */
  const liveConnections = useMemo(
    () => connections.filter((c) => c.status === "active" && enabled.has(slugOf(c))),
    [connections, enabled]
  );

  // Must match what the MCP server will actually advertise, account by account: a Slack
  // webhook connection exposes one tool, not the four the OAuth connection would -- and a
  // Generic MCP account exposes the tools discovered from its own server, which is why this
  // asks `toolsForConnection` rather than reading `provider.tools`. That array is empty for
  // a dynamic provider, so counting it undercounts every such account by all of its tools.
  const toolCount = useMemo(() => {
    return liveConnections.reduce((sum, conn) => {
      const provider = catalog.find((p) => p.id === conn.providerId);
      if (!provider) return sum;

      const available = toolsForConnection(provider, conn);
      const allow = enablementFor(conn, server).allowList;
      return (
        sum +
        (allow
          ? available.filter((t) => allow.includes(t.name)).length
          : available.length)
      );
    }, 0);
  }, [liveConnections, catalog, server]);

  const context: DashboardContext = {
    user,
    teams,
    projects,
    project,
    selectProject,
    incomingInvites,
    teamInvites,
    server,
    token,
    onToken: setToken,
    oauthClient,
    catalog,
    connections,
    activity,
    enabled,
    liveConnections,
    toolCount,
    toggleConnection,
    notify,
  };

  if (loading) {
    return (
      <div className="boot">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="app">
      <TopBar
        user={user}
        teams={teams}
        projects={projects}
        project={project}
        selectProject={selectProject}
        liveCount={liveConnections.length}
        toolCount={toolCount}
        inviteCount={incomingInvites.length}
        onSignOut={() => void signOut()}
      />

      <main className="main">
        <Outlet context={context} />
      </main>

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
